import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { API } from "../../contexts/AuthContext";

const formatError = (detail) => {
  if (!detail) return "A apărut o eroare.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  return String(detail);
};

const initialFromSchema = (fields) => {
  const out = {};
  fields.forEach((f) => {
    if (f.type === "list") out[f.name] = [];
    else if (f.type === "boolean") out[f.name] = f.default ?? false;
    else if (f.type === "number") out[f.name] = f.default ?? 0;
    else out[f.name] = f.default ?? "";
  });
  return out;
};

const toFormState = (record, fields) => {
  const out = {};
  fields.forEach((f) => {
    const v = record[f.name];
    if (f.type === "list") out[f.name] = Array.isArray(v) ? v.join(" | ") : "";
    else out[f.name] = v ?? (f.type === "boolean" ? false : "");
  });
  return out;
};

const fromFormState = (state, fields) => {
  const out = {};
  fields.forEach((f) => {
    let v = state[f.name];
    if (f.type === "list") {
      v = String(v || "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (f.type === "number") {
      v = Number(v) || 0;
    } else if (f.type === "boolean") {
      v = Boolean(v);
    }
    out[f.name] = v;
  });
  return out;
};

export default function ResourceManager({ resource }) {
  const { endpoint, fields, primaryKey, listColumns } = resource;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, "new"=create, {row}=edit
  const [formState, setFormState] = useState(initialFromSchema(fields));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${endpoint}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing("new");
    setFormState(initialFromSchema(fields));
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormState(toFormState(row, fields));
  };

  const closeForm = () => {
    setEditing(null);
  };

  const handleChange = (name, value) => {
    setFormState((s) => ({ ...s, [name]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = fromFormState(formState, fields);
    try {
      if (editing === "new") {
        await axios.post(`${API}${endpoint}`, payload);
        toast.success("Creat cu succes");
      } else {
        const key = editing[primaryKey];
        await axios.put(`${API}${endpoint}/${encodeURIComponent(key)}`, payload);
        toast.success("Actualizat cu succes");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Sigur ștergeți „${row[primaryKey]}"?`)) return;
    try {
      await axios.delete(`${API}${endpoint}/${encodeURIComponent(row[primaryKey])}`);
      toast.success("Șters");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Eroare la ștergere");
    }
  };

  return (
    <div data-testid={`resource-manager-${resource.label}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">{resource.label}</div>
          <div className="font-mono-tech text-xs text-neutral-500">
            {rows.length} înregistr{rows.length === 1 ? "are" : "ări"}
          </div>
        </div>
        <button
          onClick={openNew}
          data-testid="btn-new-record"
          className="inline-flex items-center gap-2 btn-yellow px-4 py-2.5 text-xs font-mono-tech uppercase tracking-[0.15em]"
        >
          <Plus className="h-4 w-4" /> Adaugă
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="border border-neutral-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0d0d] border-b border-neutral-800">
              <tr>
                {listColumns.map((c) => (
                  <th
                    key={c}
                    className="text-left font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-4 py-3"
                  >
                    {c}
                  </th>
                ))}
                <th className="text-right font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-4 py-3">
                  Acțiuni
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={listColumns.length + 1}
                    className="text-center text-neutral-500 py-10 font-mono-tech text-xs"
                  >
                    Niciun rezultat. Apăsați „Adaugă" pentru a crea.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`row-${row[primaryKey]}`}
                  className="border-b border-neutral-900 hover:bg-[#0d0d0d]"
                >
                  {listColumns.map((c) => (
                    <td key={c} className="px-4 py-3 text-neutral-300 max-w-md truncate">
                      {c === "active" ? (
                        <span
                          className={`font-mono-tech text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${
                            row[c] ? "text-green-400 border border-green-900" : "text-neutral-500 border border-neutral-800"
                          }`}
                        >
                          {row[c] ? "activ" : "inactiv"}
                        </span>
                      ) : (
                        String(row[c] ?? "")
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(row)}
                      data-testid={`btn-edit-${row[primaryKey]}`}
                      className="inline-flex items-center gap-1 text-neutral-400 hover:text-yellow-400 px-2 py-1"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editează
                    </button>
                    <button
                      onClick={() => onDelete(row)}
                      data-testid={`btn-delete-${row[primaryKey]}`}
                      className="inline-flex items-center gap-1 text-neutral-400 hover:text-red-400 px-2 py-1 ml-2"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Șterge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          data-testid="resource-form-overlay"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
        >
          <form
            onSubmit={onSave}
            data-testid="resource-form"
            className="bg-[#0d0d0d] border border-neutral-800 max-w-3xl w-full my-8"
          >
            <div className="flex items-center justify-between border-b border-neutral-800 px-6 py-4">
              <div>
                <div className="overline">
                  {editing === "new" ? "Adăugare" : "Editare"}
                </div>
                <div className="font-display font-semibold text-white text-lg">
                  {resource.label}
                </div>
              </div>
              <button
                type="button"
                onClick={closeForm}
                data-testid="btn-close-form"
                className="text-neutral-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5 max-h-[60vh] overflow-y-auto">
              {fields.map((f) => (
                <FormField
                  key={f.name}
                  field={f}
                  value={formState[f.name]}
                  onChange={(v) => handleChange(f.name, v)}
                  disabled={f.name === "slug" && editing !== "new"}
                />
              ))}
            </div>

            <div className="border-t border-neutral-800 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeForm}
                className="btn-outline px-5 py-2.5 text-xs font-mono-tech uppercase tracking-[0.15em]"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="btn-save-form"
                className="inline-flex items-center gap-2 btn-yellow px-5 py-2.5 text-xs font-mono-tech uppercase tracking-[0.15em] disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvează
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const FormField = ({ field, value, onChange, disabled }) => {
  const labelEl = (
    <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
      {field.label} {field.required && "*"}
    </label>
  );
  const fullSpan = field.type === "textarea" || field.type === "list";
  const cls = "w-full bg-transparent border border-neutral-800 px-3 py-2.5 text-white placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 transition-colors text-sm disabled:opacity-60";

  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        {labelEl}
        <textarea
          rows={3}
          required={field.required}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={cls + " resize-y"}
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  if (field.type === "list") {
    return (
      <div className="sm:col-span-2">
        {labelEl}
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ex: Element1 | Element2 | Element3"
          className={cls}
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  if (field.type === "boolean") {
    return (
      <div className={fullSpan ? "sm:col-span-2" : ""}>
        {labelEl}
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 accent-yellow-500"
            data-testid={`field-${field.name}`}
          />
          <span className="text-sm text-neutral-300">{value ? "Da" : "Nu"}</span>
        </label>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        {labelEl}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={cls + " bg-[#0A0A0A]"}
          data-testid={`field-${field.name}`}
        >
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt || "—"}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div>
        {labelEl}
        <input
          type="number"
          required={field.required}
          value={value ?? 0}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  return (
    <div>
      {labelEl}
      <input
        type="text"
        required={field.required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cls}
        data-testid={`field-${field.name}`}
      />
    </div>
  );
};
